import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button, Card, Checkbox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Input, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger,
  MessageBar, MessageBarActions, MessageBarBody, Select, Spinner, Table, TableBody, TableCell, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Text, Textarea,
  makeStyles, shorthands, tokens,
} from '@fluentui/react-components';
import { AddRegular, CopyRegular, DismissRegular, MoreHorizontalRegular, OpenRegular } from '@fluentui/react-icons';
import { SSO_SKIP_HELP, instanceAddressWarnings, returnAddressOf, INSTANCE_KINDS, INSTANCE_KIND_LABELS, SLUG_RE, chooseRoute, ssoState, RESERVED_SLUGS, normalizeInstanceUrl, slugFromName, type InstanceKind, type InstanceView, type InstanceWithToken, type LaunchPlan, type LaunchResult, type Route } from '@vxh/shared';
import { api } from '../api';
import { cachedProbe, probeDirect } from '../probe';
import { useAuth } from '../auth';
import { PageHeader } from '../components/PageHeader';
import { RemotePill, SsoPill, StatusPill } from '../components/StatusPill';
import { ago, fullTime } from '../format';

const useStyles = makeStyles({
  muted: { color: tokens.colorNeutralForeground3 },
  empty: { ...shorthands.padding('40px', '24px'), display: 'grid', justifyItems: 'center', ...shorthands.gap('8px'), textAlign: 'center' },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', ...shorthands.gap('14px') },
  token: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, overflowWrap: 'anywhere', ...shorthands.padding('10px', '12px'), backgroundColor: tokens.colorNeutralBackground3, ...shorthands.borderRadius('6px') },
  row: { display: 'flex', ...shorthands.gap('8px'), alignItems: 'center', flexWrap: 'wrap' },
  tableWrap: { overflowX: 'auto' },
});

export function InstancesPage() {
  const s = useStyles();
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['instances'], queryFn: () => api<InstanceView[]>('/instances'), refetchInterval: 30_000 });
  const [editing, setEditing] = useState<InstanceView | 'new' | null>(null);
  const [token, setToken] = useState<InstanceWithToken | null>(null);
  const [removing, setRemoving] = useState<InstanceView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<InstanceView | null>(null);
  // Why the person was not signed in with their hub account, when they could have been (shown after Open).
  const [ssoNotice, setSsoNotice] = useState<{ name: string; text: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['instances'] });
  const fail = (e: unknown) => setError((e as Error).message);

  const toggle = useMutation({ mutationFn: (i: InstanceView) => api(`/instances/${i.id}`, { method: 'PATCH', body: { enabled: !i.enabled } }), onSuccess: refresh, onError: fail });
  const rotate = useMutation({ mutationFn: (i: InstanceView) => api<InstanceWithToken>(`/instances/${i.id}/rotate-token`, { method: 'POST', body: {} }), onSuccess: (r) => (setToken(r), void refresh()), onError: fail });
  const remove = useMutation({ mutationFn: (i: InstanceView) => api(`/instances/${i.id}`, { method: 'DELETE' }), onSuccess: () => (setRemoving(null), void refresh()), onError: fail });

  // Open in a new tab. The tab is opened straight away (a browser blocks one opened after a wait) and sent to the address once the hub has
  // written the audit entry. The new tab is cut off from this one so the instance cannot reach back into the hub.
  // `auto` (the Open button) picks the route: the hub says what it knows (same network? tunnel up?), the browser tests the instance's own address when that
  // is the open question, and chooseRoute() (packages/shared, unit tested) decides. The menu items name a route themselves and skip all that.
  const open = async (i: InstanceView, opts: { auto?: boolean; mode?: Route; sso?: false } = {}) => {
    setError(null);
    setNotice(null);
    setSsoNotice(null);
    const w = window.open('about:blank', '_blank');
    if (w) w.opener = null;
    try {
      let mode = opts.mode;
      let hint = false;
      if (opts.auto) {
        const plan = await api<LaunchPlan>(`/instances/${i.id}/launch-plan`);
        const probeUrl = plan.directProbeUrl;
        let d = chooseRoute(plan, probeUrl ? cachedProbe(probeUrl) : undefined);
        if (d.action === 'probe' && probeUrl) d = chooseRoute(plan, await probeDirect(probeUrl));
        if (d.action === 'open') (mode = d.route), (hint = d.notice);
      }
      const launched = await api<LaunchResult>(`/instances/${i.id}/launch`, { method: 'POST', body: { ...(mode ? { mode } : {}), ...(opts.sso === false ? { sso: false } : {}) } });
      const { url } = launched;
      if (w) w.location.href = url;
      else window.location.href = url;
      if (launched.ssoSkipped) setSsoNotice({ name: i.name, text: SSO_SKIP_HELP[launched.ssoSkipped] });
      // Opened at its own address without testing it (same network): say how to go through the hub, in case the page does not load.
      if (hint) setNotice(i);
    } catch (e) {
      w?.close();
      fail(e);
    }
  };

  const rows = q.data ?? [];
  return (
    <>
      <PageHeader
        title="Instances"
        subtitle="Every Voxshift you can open: the hosted service and each customer's own installation."
        actions={isAdmin ? <Button appearance="primary" icon={<AddRegular />} onClick={() => setEditing('new')}>Register an instance</Button> : undefined}
      />
      {error && (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {ssoNotice && (
        <MessageBar intent="info" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <strong>{ssoNotice.name}:</strong> {ssoNotice.text}
          </MessageBarBody>
          <MessageBarActions containerAction={<Button appearance="transparent" aria-label="Dismiss" icon={<DismissRegular />} onClick={() => setSsoNotice(null)} />} />
        </MessageBar>
      )}
      {notice && (
        <MessageBar intent="info" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            Opened {notice.name} at its own address. Did not load?{' '}
            <Button appearance="transparent" size="small" style={{ minWidth: 0, padding: 0 }} onClick={() => void open(notice, { mode: 'remote' })}>Open through the hub</Button>
          </MessageBarBody>
          <MessageBarActions containerAction={<Button appearance="transparent" aria-label="Dismiss" icon={<DismissRegular />} onClick={() => setNotice(null)} />} />
        </MessageBar>
      )}
      <Card>
        {q.isLoading ? (
          <Spinner label="Loading" />
        ) : q.isError ? (
          <MessageBar intent="error"><MessageBarBody>{(q.error as Error).message}</MessageBarBody></MessageBar>
        ) : rows.length === 0 ? (
          <div className={s.empty}>
            <Text weight="semibold">No instances yet</Text>
            <Text className={s.muted}>{isAdmin ? 'Register the hosted service and each customer installation to see them here.' : 'An administrator has not registered any instances yet.'}</Text>
          </div>
        ) : (
          <div className={s.tableWrap}>
            <Table aria-label="Instances">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Through the hub</TableHeaderCell>
                  <TableHeaderCell>Single sign-on</TableHeaderCell>
                  <TableHeaderCell>Version</TableHeaderCell>
                  <TableHeaderCell>Last seen</TableHeaderCell>
                  <TableHeaderCell aria-label="Actions" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>
                      <TableCellLayout description={i.customerName ? `${i.customerName} · ${i.baseUrl.replace(/^https?:\/\//, '')}` : i.baseUrl.replace(/^https?:\/\//, '')}>
                        <Text weight="semibold">{i.name}</Text>
                      </TableCellLayout>
                    </TableCell>
                    <TableCell>{INSTANCE_KIND_LABELS[i.kind]}</TableCell>
                    <TableCell>
                      <StatusPill status={i.status} />
                      {i.health === 'degraded' && i.status === 'online' && <Text size={200} className={s.muted}> · degraded</Text>}
                    </TableCell>
                    <TableCell><RemotePill remote={i.remoteAccess} /></TableCell>
                    <TableCell><SsoPill state={ssoState(i)} /></TableCell>
                    <TableCell>{i.version ?? '-'}</TableCell>
                    <TableCell title={i.lastSeenAt ? fullTime(i.lastSeenAt) : undefined}>{ago(i.lastSeenAt)}</TableCell>
                    <TableCell>
                      <div className={s.row}>
                        <Button appearance="primary" size="small" icon={<OpenRegular />} disabled={!i.enabled} onClick={() => void open(i, { auto: true })}>Open</Button>
                        <Menu>
                          <MenuTrigger disableButtonEnhancement>
                            <Button appearance="subtle" size="small" icon={<MoreHorizontalRegular />} aria-label={`More actions for ${i.name}`} />
                          </MenuTrigger>
                          <MenuPopover>
                            <MenuList>
                              {/* Ways to open it that skip the automatic choice. */}
                              <MenuItem disabled={!i.enabled} onClick={() => void open(i, { mode: 'direct' })}>Open at its own address</MenuItem>
                              <MenuItem disabled={!i.enabled || i.remoteAccess !== 'connected'} onClick={() => void open(i, { mode: 'remote' })}>Open through the hub</MenuItem>
                              <MenuItem disabled={!i.enabled} onClick={() => void open(i, { auto: true, sso: false })}>Open without single sign-on</MenuItem>
                              {isAdmin && (
                                <>
                                  <MenuItem onClick={() => setEditing(i)}>Edit details</MenuItem>
                                  <MenuItem onClick={() => rotate.mutate(i)}>Rotate token</MenuItem>
                                  <MenuItem onClick={() => toggle.mutate(i)}>{i.enabled ? 'Switch off' : 'Switch on'}</MenuItem>
                                  <MenuItem onClick={() => setRemoving(i)}>Remove</MenuItem>
                                </>
                              )}
                            </MenuList>
                          </MenuPopover>
                        </Menu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {editing && (
        <InstanceDialog
          instance={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(r) => {
            setEditing(null);
            if (r) setToken(r);
            void refresh();
          }}
        />
      )}
      {token && <TokenDialog data={token} onClose={() => setToken(null)} />}
      {removing && (
        <Dialog open onOpenChange={(_, d) => !d.open && setRemoving(null)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Remove {removing.name}?</DialogTitle>
              <DialogContent>
                It disappears from the hub and its token stops working. The instance itself is not touched and keeps running; you can register it again later.
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={() => setRemoving(null)}>Cancel</Button>
                <Button appearance="primary" disabled={remove.isPending} onClick={() => remove.mutate(removing)}>Remove</Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </>
  );
}

function InstanceDialog({ instance, onClose, onSaved }: { instance: InstanceView | null; onClose: () => void; onSaved: (r: InstanceWithToken | null) => void }) {
  const s = useStyles();
  const [name, setName] = useState(instance?.name ?? '');
  const [customerName, setCustomerName] = useState(instance?.customerName ?? '');
  const [kind, setKind] = useState<InstanceKind>(instance?.kind ?? 'customer_premises');
  const [baseUrl, setBaseUrl] = useState(instance?.baseUrl ?? '');
  const [slug, setSlug] = useState(instance?.slug ?? '');
  // Until the short name is typed by hand, it follows the name.
  const [slugEdited, setSlugEdited] = useState(!!instance);
  const hubHost = window.location.hostname;
  const [notes, setNotes] = useState(instance?.notes ?? '');
  const [mailRelay, setMailRelay] = useState(instance?.mailRelay ?? true);
  const [ssoEnabled, setSsoEnabled] = useState(instance?.ssoEnabled ?? true);
  const [error, setError] = useState<string | null>(null);

  const addr = baseUrl.trim() ? normalizeInstanceUrl(baseUrl) : null;
  const addrError = addr && !addr.ok ? addr.error : null;
  const addrWarnings = instanceAddressWarnings(baseUrl, { hubHost, saved: instance?.baseUrl });
  const slugError = slug && (!SLUG_RE.test(slug) ? 'Use 2 to 40 lower-case letters, digits or hyphens (not at the ends).' : (RESERVED_SLUGS as readonly string[]).includes(slug) ? 'That name is reserved for the hub.' : null);
  const valid = name.trim().length >= 2 && addr?.ok === true && !slugError;

  const save = useMutation<InstanceWithToken | InstanceView>({
    mutationFn: () =>
      instance
        ? api<InstanceView>(`/instances/${instance.id}`, { method: 'PATCH', body: { name, customerName, kind, baseUrl, slug: slug || undefined, notes, mailRelay, ssoEnabled } })
        : api<InstanceWithToken>('/instances', { method: 'POST', body: { name, customerName, kind, baseUrl, slug: slug || undefined, notes, mailRelay, ssoEnabled } }),
    onSuccess: (r) => onSaved(instance ? null : (r as InstanceWithToken)),
    onError: (e) => setError((e as Error).message),
  });

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{instance ? `Edit ${instance.name}` : 'Register an instance'}</DialogTitle>
          <DialogContent className={s.grid}>
            <Field label="Name" required hint="How staff will recognise it, e.g. AT&T Voice Consult.">
              <Input value={name} onChange={(_, d) => { setName(d.value); if (!slugEdited) setSlug(slugFromName(d.value)); }} autoFocus />
            </Field>
            <Field label="Customer">
              <Input value={customerName} onChange={(_, d) => setCustomerName(d.value)} />
            </Field>
            <Field label="Type">
              <Select value={kind} onChange={(_, d) => setKind(d.value as InstanceKind)}>
                {INSTANCE_KINDS.map((k) => (
                  <option key={k} value={k}>{INSTANCE_KIND_LABELS[k]}</option>
                ))}
              </Select>
            </Field>
            <Field
              label="Address"
              required
              hint={addr?.ok ? `Single sign-on sends people back to ${returnAddressOf(addr.url)}. This must be exactly the Web address set on the installation itself (Administration > Web address): same https://, same www, same port.` : 'Where people open it, e.g. https://voice.contoso.com. It must be exactly the Web address set on the installation itself (Administration > Web address), or hub sign-in is refused with "the return address does not match".'}
              validationState={addrError ? 'error' : addrWarnings.length ? 'warning' : 'none'}
              validationMessage={addrError ?? (addrWarnings.length ? addrWarnings.join(' ') : undefined)}
            >
              <Input value={baseUrl} onChange={(_, d) => setBaseUrl(d.value)} placeholder="https://" />
            </Field>
            <Field label="Short name" hint={slug && !slugError ? `Opened through the hub at https://${slug}.${hubHost}` : 'Used in the address people open it at through the hub.'} validationState={slugError ? 'error' : 'none'} validationMessage={slugError ?? undefined}>
              <Input value={slug} onChange={(_, d) => { setSlug(d.value.toLowerCase()); setSlugEdited(true); }} />
            </Field>
            <Checkbox checked={mailRelay} onChange={(_, d) => setMailRelay(!!d.checked)} label="Allow this instance to send email through the hub's mail service" />
            <Checkbox checked={ssoEnabled} onChange={(_, d) => setSsoEnabled(!!d.checked)} label="Let people sign in to this instance with their hub account (single sign-on)" />
            <Field label="Notes">
              <Textarea value={notes} onChange={(_, d) => setNotes(d.value)} rows={3} />
            </Field>
            {error && (
              <MessageBar intent="error">
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>Cancel</Button>
            <Button appearance="primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{instance ? 'Save' : 'Register'}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** Shown once: the instance's token. The hub keeps only a hash, so it cannot be shown again, only replaced. */
function TokenDialog({ data, onClose }: { data: InstanceWithToken; onClose: () => void }) {
  const s = useStyles();
  const [copied, setCopied] = useState(false);
  const copy = () => void navigator.clipboard?.writeText(data.token).then(() => setCopied(true), () => setCopied(false));
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Token for {data.instance.name}</DialogTitle>
          <DialogContent className={s.grid}>
            <MessageBar intent="warning">
              <MessageBarBody>Copy it now. For safety the hub keeps only a fingerprint, so it cannot be shown again. If it is lost, rotate it to get a new one.</MessageBarBody>
            </MessageBar>
            <div className={s.token}>{data.token}</div>
            <div className={s.row}>
              <Button icon={<CopyRegular />} onClick={copy}>{copied ? 'Copied' : 'Copy token'}</Button>
            </div>
            <Text className={s.muted}>
              The instance reports to <b>{window.location.origin}</b> with this token, once a minute, so it shows as online here. It sends a version number and a health word, never customer data.
            </Text>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>Done</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
